import React from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import { Button } from './ui/button';
import {
  Plus, Loader2, Upload, FileDown, FileSpreadsheet,
  CheckSquare, Calculator, RefreshCw, Zap, ShieldCheck, Layers,
} from 'lucide-react';

export function ShipmentsToolbar({
  showInternalColumns,
  isAdmin,
  selectedShipments,
  exportingExcel,
  prepedUploading,
  prepedFileRef,
  bulkRevalidating,
  calculatingEtas,
  onExportExcel,
  onOpenExcelImport,
  onLegacyExport,
  onPrePedimentoUpload,
  onOpenBulkProforma,
  onOpenBulkPedimento,
  onRepairNow,
  onOpenBulkEdit,
  onBulkRevalidate,
  onCalculateEtas,
}) {
  const { t } = useI18n();

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {showInternalColumns && isAdmin && (
        <>
          <Button
            variant="outline"
            onClick={() => onExportExcel(selectedShipments.length > 0)}
            disabled={exportingExcel}
            data-testid="excel-export-btn"
            title={selectedShipments.length > 0 ? t('shipments.exportSelected', { count: selectedShipments.length }) : t('shipments.exportAll')}
          >
            {exportingExcel ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <FileDown size={16} className="mr-2" />
            )}
            {selectedShipments.length > 0 ? t('shipments.excelLabelCount', { count: selectedShipments.length }) : t('shipments.excelLabel')}
          </Button>
          <Button
            variant="outline"
            onClick={onOpenExcelImport}
            data-testid="excel-import-btn"
          >
            <Upload size={16} className="mr-2" />
            {t('shipments.uploadExcel')}
          </Button>
          <input
            ref={prepedFileRef}
            type="file"
            accept=".pdf"
            className="hidden"
            onChange={onPrePedimentoUpload}
            data-testid="preped-file-input"
          />
          <Button
            variant="outline"
            onClick={() => prepedFileRef.current?.click()}
            disabled={prepedUploading}
            title={t('shipments.prePedimentoTitle')}
            data-testid="preped-upload-btn"
          >
            {prepedUploading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Zap size={16} className="mr-2 text-blue-400" />
            )}
            {t('shipments.prePedimento')}
          </Button>
          <Button
            variant="outline"
            onClick={onOpenBulkProforma}
            title={t('shipments.prePedimentoBulkTitle')}
            data-testid="bulk-proforma-btn"
          >
            <Layers size={16} className="mr-2 text-blue-400" />
            {t('shipments.prePedimentoBulk')}
          </Button>
          <Button
            variant="outline"
            onClick={onOpenBulkPedimento}
            title="Subir múltiples PDFs de pedimento — auto-asigna a cada embarque/contenedor"
            data-testid="bulk-pedimento-btn"
          >
            <Layers size={16} className="mr-2 text-indigo-400" />
            Pedimentos (lote)
          </Button>
          <Button
            variant="outline"
            onClick={onRepairNow}
            title={t('shipments.repairTitle')}
            data-testid="repair-sync-btn"
          >
            <RefreshCw size={16} className="mr-2 text-emerald-400" />
            {t('shipments.repairSync')}
          </Button>
        </>
      )}

      {showInternalColumns && !isAdmin && (
        <Button variant="outline" onClick={onLegacyExport} data-testid="export-btn">
          <FileSpreadsheet size={16} className="mr-2" />
          {t('common.download')}
        </Button>
      )}

      {isAdmin && selectedShipments.length > 0 && (
        <Button
          variant="secondary"
          onClick={onOpenBulkEdit}
          data-testid="bulk-edit-btn"
        >
          <CheckSquare size={16} className="mr-2" />
          {t('shipments.bulkEditCount')} ({selectedShipments.length})
        </Button>
      )}

      {isAdmin && selectedShipments.length > 0 && (
        <Button
          variant="outline"
          onClick={onBulkRevalidate}
          disabled={bulkRevalidating}
          title={t('shipments.bulkRevalidateTitle')}
          data-testid="bulk-revalidate-btn"
        >
          {bulkRevalidating ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <ShieldCheck size={16} className="mr-2" />
          )}
          {t('shipments.bulkRevalidate')} ({selectedShipments.length})
        </Button>
      )}

      {isAdmin && (
        <Button
          variant="outline"
          onClick={onCalculateEtas}
          disabled={calculatingEtas}
          data-testid="calculate-etas-btn"
          title={t('shipments.etasTitle')}
        >
          {calculatingEtas ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Calculator size={16} className="mr-2" />
          )}
          {t('shipments.calcEtas')}
        </Button>
      )}

      {showInternalColumns && (
        <Link to="/shipments/new">
          <Button data-testid="new-shipment-btn">
            <Plus size={16} className="mr-2" />
            {t('shipments.newShipment')}
          </Button>
        </Link>
      )}
    </div>
  );
}
